// Wave 4b — Project-scoped Permits management page.
// Route: /projects/:id/permits
// RBAC: any authenticated tenant user can view; create/edit gated by role.
// Truthful adapter-state chips. Dry-run submission preview by default.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  ClipboardCheck, Plus, RefreshCw, Send, ArrowLeft, MapPin, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";

const PERMIT_TYPES = [
  "building", "electrical", "plumbing", "mechanical",
  "demolition", "zoning", "grading", "roofing", "other",
];
const PROVIDERS = [
  { key: "accela", label: "Accela Civic Platform" },
  { key: "tyler_energov", label: "Tyler EnerGov" },
  { key: "opengov_cloud", label: "OpenGov Cloud" },
  { key: "etrakit", label: "eTRAKiT / CentralSquare" },
  { key: "citizenserve", label: "Citizenserve" },
  { key: "manual", label: "Manual (no adapter)" },
];
const STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-800 border-zinc-300",
  submitted: "bg-blue-50 text-blue-900 border-blue-200",
  in_review: "bg-blue-50 text-blue-900 border-blue-200",
  corrections_required: "bg-amber-50 text-amber-900 border-amber-200",
  approved: "bg-emerald-50 text-emerald-900 border-emerald-200",
  issued: "bg-emerald-50 text-emerald-900 border-emerald-200",
  inspection_scheduled: "bg-blue-50 text-blue-900 border-blue-200",
  final: "bg-emerald-50 text-emerald-900 border-emerald-200",
  rejected: "bg-red-50 text-red-900 border-red-200",
  expired: "bg-red-50 text-red-900 border-red-200",
  withdrawn: "bg-zinc-100 text-zinc-800 border-zinc-300",
};

const ADAPTER_STATE_STYLES = {
  CONNECTED: { bg: "#ECFDF5", text: "#065F46", border: "#A7F3D0", label: "Connected" },
  NOT_CONFIGURED: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Not Configured" },
  CONNECTING: { bg: "#EFF6FF", text: "#1E3A8A", border: "#BFDBFE", label: "Connecting" },
  VERIFYING: { bg: "#EFF6FF", text: "#1E3A8A", border: "#BFDBFE", label: "Verifying" },
  DEGRADED: { bg: "#FFF7ED", text: "#9A3412", border: "#FFEDD5", label: "Degraded" },
  ERROR: { bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", label: "Error" },
  REVOKED: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Revoked" },
  NOT_APPLICABLE: { bg: "#F4F4F5", text: "#3F3F46", border: "#D4D4D8", label: "Manual" },
};

function AdapterChip({ state }) {
  const upper = (state || "NOT_CONFIGURED").toUpperCase();
  const meta = ADAPTER_STATE_STYLES[upper] || ADAPTER_STATE_STYLES.NOT_CONFIGURED;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-nav font-semibold uppercase tracking-wider border"
      style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}
      data-testid={`adapter-chip-${upper}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: meta.text }} />
      {meta.label}
    </span>
  );
}

const CREATE_ROLES = new Set(["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROJECT_MANAGER"]);
const SUBMIT_ROLES = new Set(["OWNER_SUPER_ADMIN", "ADMINISTRATOR"]);

const emptyForm = () => ({
  jurisdiction_provider_key: "accela",
  jurisdiction_name: "",
  permit_type: "building",
  description: "",
  address: { street: "", city: "", state: "", postal_code: "", county: "" },
  applicant: { name: "", license_number: "", contact_email: "", contact_phone: "" },
  fees_estimated: "",
  provider_reference_url: "",
  notes: "",
});

export default function ProjectPermits() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const canCreate = user && CREATE_ROLES.has(user.role_key);
  const canSubmit = user && SUBMIT_ROLES.has(user.role_key);

  const [project, setProject] = useState(null);
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [lookup, setLookup] = useState(null);
  const [submitPreview, setSubmitPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, perms] = await Promise.all([
        api.get(`/projects/${projectId}`).catch(() => ({ data: null })),
        api.get(`/permits`, { params: { project_id: projectId } }),
      ]);
      setProject(proj.data);
      setPermits(perms.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  async function lookupJurisdiction() {
    const { state, county, city } = form.address;
    if (!state) { toast.error("Enter a 2-letter state code first."); return; }
    try {
      const r = await api.post("/permits/lookup-jurisdiction", {
        state: state.toUpperCase(), county: county || null, city: city || null,
      });
      setLookup(r.data);
      if (r.data.found) {
        setForm((f) => ({ ...f, jurisdiction_provider_key: r.data.recommended_provider_key }));
        toast.success(`Recommended: ${r.data.recommended_provider_key} (${r.data.adapter_state})`);
      } else {
        toast.message(r.data.message || "No jurisdiction map entry found; using manual.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function createPermit(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        project_id: projectId,
        fees_estimated: form.fees_estimated === "" ? null : Number(form.fees_estimated),
      };
      await api.post("/permits", payload);
      toast.success("Permit record created.");
      setShowForm(false);
      setForm(emptyForm());
      setLookup(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function syncStatus(pid) {
    try {
      const r = await api.post(`/permits/${pid}/sync-status`);
      const msg = r.data.message || `Sync result: ${r.data.status}`;
      if (r.data.status === "CONNECTED" || r.data.status === "SUBMITTED_MANUAL") {
        toast.success(msg);
      } else {
        toast.message(msg);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function openSubmitPreview(permit) {
    try {
      const r = await api.post(`/permits/${permit.id}/submit-application`, { confirm: false });
      setSubmitPreview({ permit, preview: r.data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function confirmSubmit() {
    if (!submitPreview) return;
    try {
      const r = await api.post(
        `/permits/${submitPreview.permit.id}/submit-application`,
        { confirm: true },
      );
      toast.success(r.data.message || "Permit submitted.");
      setSubmitPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AppShell
      title={project ? `${project.name} — Permits` : "Permits"}
      subtitle="Jurisdictions · Applications · Sync"
      actions={
        <div className="flex gap-2">
          <Link to={`/projects/${projectId}`}>
            <Button variant="outline" className="border-erf-borderCream" data-testid="permits-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Project
            </Button>
          </Link>
          {canCreate && (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
              data-testid="permit-new-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add permit
            </Button>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="font-mono text-xs text-erf-dark/60" data-testid="permits-loading">Loading…</div>
      ) : permits.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No permits recorded for this project"
          subtitle={canCreate
            ? "Add a permit application to track filing, review, inspections, and issuance."
            : "Permit records will appear here once created by a Project Manager, Administrator, or Owner."}
        />
      ) : (
        <div className="grid gap-3" data-testid="permits-list">
          {permits.map((p) => (
            <Card key={p.id} className="border-erf-borderCream" data-testid={`permit-row-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
                        {p.permit_type}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-nav font-semibold uppercase tracking-wider border ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`}
                        data-testid={`permit-status-${p.id}`}
                      >
                        {p.status.replace(/_/g, " ")}
                      </span>
                      <AdapterChip state={p.adapter_state} />
                    </div>
                    <div className="mt-1 font-display font-semibold text-erf-dark">
                      {p.jurisdiction_name}
                    </div>
                    <div className="text-xs font-nav text-erf-dark/60 mt-1">
                      {p.address?.street}, {p.address?.city}, {p.address?.state}
                      {p.address?.county ? ` · ${p.address.county} County` : ""}
                    </div>
                    <div className="text-[11px] font-mono text-erf-dark/50 mt-1">
                      Provider: {p.jurisdiction_provider_key} · Application ID: {p.application_id || "—"}
                    </div>
                    {p.last_synced_at && (
                      <div className="text-[11px] font-mono text-erf-dark/50 mt-1">
                        Last synced: {p.last_synced_at.slice(0, 19).replace("T", " ")}
                        {p.last_sync_result?.status ? ` · ${p.last_sync_result.status}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {canCreate && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => syncStatus(p.id)}
                        className="border-erf-borderCream"
                        data-testid={`permit-sync-${p.id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync status
                      </Button>
                    )}
                    {canSubmit && p.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={() => openSubmitPreview(p)}
                        className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                        data-testid={`permit-submit-${p.id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1" /> Submit…
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-erf-borderCream/60 text-sm font-nav text-erf-dark/80">
                  {p.description}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ─── New permit form ────────────────────────────────────────────── */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl bg-erf-cream" data-testid="permit-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New permit application</DialogTitle>
          </DialogHeader>
          <form onSubmit={createPermit} className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">Permit type</Label>
                <select
                  value={form.permit_type}
                  onChange={(e) => setForm({ ...form, permit_type: e.target.value })}
                  className="w-full h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white"
                  data-testid="permit-form-type"
                >
                  {PERMIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Jurisdiction adapter</Label>
                <select
                  value={form.jurisdiction_provider_key}
                  onChange={(e) => setForm({ ...form, jurisdiction_provider_key: e.target.value })}
                  className="w-full h-9 px-3 text-sm border border-erf-borderCream rounded-md bg-white"
                  data-testid="permit-form-provider"
                >
                  {PROVIDERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Jurisdiction name (e.g. City of Philadelphia L&I)</Label>
              <Input
                required value={form.jurisdiction_name}
                onChange={(e) => setForm({ ...form, jurisdiction_name: e.target.value })}
                data-testid="permit-form-jurisdiction"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">Street</Label>
                <Input
                  required value={form.address.street}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })}
                  data-testid="permit-form-street"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">City</Label>
                <Input
                  required value={form.address.city}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
                  data-testid="permit-form-city"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">State (2 letters)</Label>
                <Input
                  required maxLength={2} value={form.address.state}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value.toUpperCase() } })}
                  data-testid="permit-form-state"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">County (opt.)</Label>
                <Input
                  value={form.address.county}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, county: e.target.value } })}
                  data-testid="permit-form-county"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="button" size="sm" variant="outline"
                onClick={lookupJurisdiction}
                className="border-erf-borderCream"
                data-testid="permit-lookup-btn"
              >
                <MapPin className="w-3.5 h-3.5 mr-1" /> Lookup recommended provider
              </Button>
            </div>
            {lookup && (
              <div className="text-[12px] font-mono bg-white border border-erf-borderCream rounded-md p-3" data-testid="permit-lookup-result">
                {lookup.found ? (
                  <>
                    Recommended: <strong>{lookup.recommended_provider_key}</strong> ·{" "}
                    Adapter state: <AdapterChip state={lookup.adapter_state} />
                    {lookup.notes && <div className="mt-1 text-erf-dark/70">{lookup.notes}</div>}
                  </>
                ) : (
                  <span className="text-erf-dark/70">{lookup.message}</span>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase">Applicant name</Label>
              <Input
                required value={form.applicant.name}
                onChange={(e) => setForm({ ...form, applicant: { ...form.applicant, name: e.target.value } })}
                data-testid="permit-form-applicant"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Description</Label>
              <textarea
                required rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-erf-borderCream rounded-md bg-white font-nav"
                data-testid="permit-form-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">Fees estimated (opt.)</Label>
                <Input
                  type="number" step="0.01" value={form.fees_estimated}
                  onChange={(e) => setForm({ ...form, fees_estimated: e.target.value })}
                  data-testid="permit-form-fees"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Provider reference URL (opt.)</Label>
                <Input
                  value={form.provider_reference_url}
                  onChange={(e) => setForm({ ...form, provider_reference_url: e.target.value })}
                  data-testid="permit-form-ref-url"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} data-testid="permit-form-cancel">
                Cancel
              </Button>
              <Button type="submit" className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="permit-form-save">
                Save permit record
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Submit preview + confirm ───────────────────────────────────── */}
      <Dialog open={!!submitPreview} onOpenChange={(o) => !o && setSubmitPreview(null)}>
        <DialogContent className="max-w-xl bg-erf-cream" data-testid="permit-submit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Confirm submission — dry-run preview</DialogTitle>
          </DialogHeader>
          {submitPreview && (
            <div className="grid gap-3 text-sm font-nav">
              <div className="bg-white border border-erf-borderCream rounded-md p-4 text-xs font-mono whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                {JSON.stringify(submitPreview.preview, null, 2)}
              </div>
              {submitPreview.preview.blockers?.length > 0 && (
                <div className="border border-red-200 bg-red-50 text-red-900 rounded-md p-3">
                  <div className="font-semibold mb-1">Blockers:</div>
                  <ul className="text-xs">
                    {submitPreview.preview.blockers.map((b, i) => (
                      <li key={i}>· <strong>{b.field}:</strong> {b.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs font-mono text-erf-dark/70">
                Submitting will write to the immutable audit ledger and update the permit's status.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSubmitPreview(null)} data-testid="permit-submit-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={confirmSubmit}
                  disabled={submitPreview.preview.blockers?.length > 0 || !canSubmit}
                  className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                  data-testid="permit-submit-confirm"
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> Confirm submission
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
